import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { 
  FiStar, FiCheckCircle, FiAlertCircle, FiHeart, FiSmile, 
  FiCoffee, FiLoader
} from 'react-icons/fi';

function FeedbackForm() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { alert } = useModal();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    food_rating: null,
    service_rating: null,
    ambiance_rating: null,
    comments: '',
    suggestions: ''
  });
  const [hoveredRating, setHoveredRating] = useState({
    food: null,
    service: null,
    ambiance: null
  });

  useEffect(() => {
    fetchFeedbackInfo();
  }, [token]);

  const fetchFeedbackInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/feedback/token/${token}`);
      
      if (response.data.already_submitted) {
        setAlreadySubmitted(true);
      }
      
      setFeedback(response.data.feedback);
    } catch (error) {
      console.error('Failed to fetch feedback info:', error);
      if (error.response?.status === 404 || error.response?.status === 410) {
        setFeedback(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRatingClick = (type, rating) => {
    setFormData(prev => ({ ...prev, [type]: rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.food_rating && !formData.service_rating && !formData.ambiance_rating) {
      await alert('Please provide at least one rating', 'Rating Required', 'warning');
      return;
    }

    try {
      setSubmitting(true);
      await api.post(`/api/feedback/token/${token}/submit`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      await alert(error.response?.data?.error || 'Failed to submit feedback. Please try again.', 'Error', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ type, value, onRatingClick, hovered, onHover }) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRatingClick(type, star);
            }}
            onMouseEnter={() => onHover(type, star)}
            onMouseLeave={() => onHover(type, null)}
            className={`transition-all cursor-pointer p-1 hover:scale-110 active:scale-95 ${
              (hovered || value) >= star
                ? 'text-yellow-400'
                : 'text-gray-300 hover:text-yellow-300'
            }`}
            style={{ touchAction: 'manipulation' }}
          >
            <FiStar 
              className={`h-8 w-8 ${
                (hovered || value) >= star ? 'fill-current' : ''
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FiLoader className="h-12 w-12 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading feedback form...</p>
        </div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <FiAlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Not Found</h1>
          <p className="text-gray-600 mb-4">
            This feedback link is invalid or has expired.
          </p>
          <p className="text-sm text-gray-500">
            Please contact the restaurant staff for a new feedback link.
          </p>
        </div>
      </div>
    );
  }

  if (alreadySubmitted || submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <FiCheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-4">
            {alreadySubmitted 
              ? 'Your feedback has already been submitted.'
              : 'Your feedback has been submitted successfully.'}
          </p>
          <p className="text-sm text-gray-500">
            We appreciate your input and will use it to improve our service.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-6">
            <h1 className="text-3xl font-bold mb-2">Share Your Experience</h1>
            <p className="text-gray-300">Your feedback helps us serve you better</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Food Rating */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <FiCoffee className="h-6 w-6 text-orange-500" />
                <label className="text-lg font-semibold text-gray-900">Food Quality</label>
              </div>
              <StarRating
                type="food_rating"
                value={formData.food_rating}
                hovered={hoveredRating.food}
                onRatingClick={handleRatingClick}
                onHover={(type, value) => setHoveredRating(prev => ({ ...prev, [type]: value }))}
              />
              {formData.food_rating && (
                <p className="text-sm text-gray-600">
                  {formData.food_rating === 5 ? 'Excellent!' :
                   formData.food_rating === 4 ? 'Very Good' :
                   formData.food_rating === 3 ? 'Good' :
                   formData.food_rating === 2 ? 'Fair' : 'Needs Improvement'}
                </p>
              )}
            </div>

            {/* Service Rating */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <FiHeart className="h-6 w-6 text-red-500" />
                <label className="text-lg font-semibold text-gray-900">Service Quality</label>
              </div>
              <StarRating
                type="service_rating"
                value={formData.service_rating}
                hovered={hoveredRating.service}
                onRatingClick={handleRatingClick}
                onHover={(type, value) => setHoveredRating(prev => ({ ...prev, [type]: value }))}
              />
              {formData.service_rating && (
                <p className="text-sm text-gray-600">
                  {formData.service_rating === 5 ? 'Outstanding Service!' :
                   formData.service_rating === 4 ? 'Great Service' :
                   formData.service_rating === 3 ? 'Good Service' :
                   formData.service_rating === 2 ? 'Average Service' : 'Needs Improvement'}
                </p>
              )}
            </div>

            {/* Ambiance Rating */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <FiSmile className="h-6 w-6 text-blue-500" />
                <label className="text-lg font-semibold text-gray-900">Ambiance</label>
              </div>
              <StarRating
                type="ambiance_rating"
                value={formData.ambiance_rating}
                hovered={hoveredRating.ambiance}
                onRatingClick={handleRatingClick}
                onHover={(type, value) => setHoveredRating(prev => ({ ...prev, [type]: value }))}
              />
              {formData.ambiance_rating && (
                <p className="text-sm text-gray-600">
                  {formData.ambiance_rating === 5 ? 'Perfect Atmosphere!' :
                   formData.ambiance_rating === 4 ? 'Very Nice' :
                   formData.ambiance_rating === 3 ? 'Good' :
                   formData.ambiance_rating === 2 ? 'Okay' : 'Needs Improvement'}
                </p>
              )}
            </div>

            {/* Comments */}
            <div className="space-y-2">
              <label className="block text-lg font-semibold text-gray-900">
                Your Comments
              </label>
              <textarea
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                rows={5}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 resize-none"
                placeholder="Tell us about your experience..."
              />
            </div>

            {/* Suggestions */}
            <div className="space-y-2">
              <label className="block text-lg font-semibold text-gray-900">
                Suggestions (Optional)
              </label>
              <textarea
                value={formData.suggestions}
                onChange={(e) => setFormData({ ...formData, suggestions: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 resize-none"
                placeholder="Any suggestions to help us improve?"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || (!formData.food_rating && !formData.service_rating && !formData.ambiance_rating)}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <FiLoader className="h-5 w-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FiCheckCircle className="h-5 w-5" />
                  Submit Feedback
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default FeedbackForm;

